import { AfterViewInit, Component, OnInit, computed, signal, WritableSignal, OnDestroy, ChangeDetectorRef, effect, ViewChild, ElementRef, Output, EventEmitter, Input } from '@angular/core';
import { LoaderService } from '../../services/loader.service';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { SelectedComponent } from "../../shared/selected/selected.component";
import { ListsService } from '../../services/lists.service';
import { LocationsComponent } from "../../shared/locations/locations.component";
import { ConfigInput, ConfigSelected } from '../../interfaces/interfaces';
import { InputComponent } from "../../shared/input/input.component";
import { IndependentComponent } from "../../shared/independent/independent.component";
import { AutomaticDebitComponent } from "../../shared/automatic-debit/automatic-debit.component";
import { AssociatedComponent } from "../../shared/associated/associated.component";
import { PensionersComponent } from "../../shared/pensioners/pensioners.component";
import { AgriculturalComponent } from "../../shared/agricultural/agricultural.component";
import { SecurityService } from '../../services/security.service';
import { Router } from '@angular/router';
import { RouteService } from '../../services/route.service';
import { toast } from 'ngx-sonner';
import { HelpService } from '../../services/help.service';
import { WowComponent } from "app/shared/wow/wow.component";
import { ADDRESS_INPUTS, ADDRESS_SELECTORS, MAIN_INCOME_MAX, MAIN_INCOME_MIN, isMainIncomeInRange } from 'app/config/constants.config';
import {
  buildPreloadedConfig,
  buildPreloadedSelectConfig,
  coerceSelectRawValue,
  hasPreloadedValue,
  normalizeFieldValue,
  resolveRawFieldValue,
} from '../../shared/utils/field-preload.utils';
import { collapseConsecutiveSpaces, hasMinAddressLength } from '../../shared/utils/address-normalize.utils';

interface FieldValidationState {
  originalValue: any;
  currentValue: any;
  isDirty: boolean;
  isValid: boolean;
  hasChanged: boolean;
}

@Component({
  selector: 'app-address',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    CommonModule,
    SelectedComponent,
    LocationsComponent,
    InputComponent,
    IndependentComponent,
    AutomaticDebitComponent,
    AssociatedComponent,
    PensionersComponent,
    AgriculturalComponent,
    WowComponent
  ],
  templateUrl: './address.component.html',
  styleUrl: './address.component.css'
})
export class AddressComponent implements OnInit, AfterViewInit, OnDestroy {
  form: FormGroup;
  resetTimer = signal(false);
  asotipId = signal(0);
  isValidAddress = signal(false);
  allowAddres = false;
  private confirmedAddress: string | null = null;
  private lastProcessedAddress: string | null = null;
  private lastAddressValidation: 'CORRECTA' | 'INCORRECTA' | null = null;
  private inflightAddress: string | null = null;
  private inflightNormalization: Promise<any> | null = null;
  showQuotaModal = signal(false);
  defaultData: WritableSignal<any> = signal({});
  nextRoute: WritableSignal<string> = signal('');

  @ViewChild('address') addressInput!: ElementRef<HTMLInputElement>;
  isChecked = false;

  @Output() validData = new EventEmitter<boolean>();
  @Input() isCheckData = false;
  @Input() dataLastOnboarding: any;

  isLightVersion = signal(false);
  showWowSignal = signal(false);

  dataInputs = ADDRESS_INPUTS;
  dataSelected = ADDRESS_SELECTORS;

  private fieldValidationMap = new Map<string, FieldValidationState>();
  private validationTick = signal(0);

  private originalValues: any = {};

  lightVersionIncomeConfig: ConfigInput = {
      label: '¿Cuál es tu ingreso por la actividad económica principal?',
      subLabel: '',
      formName: 'income',
      placeholder: '',
      type: 'number',
      defaultValue: '',
      enabled: true,
      validation: [Validators.required, Validators.min(MAIN_INCOME_MIN), Validators.max(MAIN_INCOME_MAX)],
      textError: 'Por favor ingresa un valor válido',
      showBlocker: true,
      originalValue: null,
      isDirty: false,
      isValid: true
  };

  isValidData = computed(() => {
      this.validationTick();
      this.defaultData();
      this.asotipId();

      if (this.isCheckData) {
          const valid = this.validateAllData();
          this.validData.emit(valid);
          return valid;
      }

      return this.validateAllData();
  });

  lightDataInputs = computed(() => {
      if (!this.isLightVersion()) return this.dataInputs();
      return this.dataInputs().filter((input: any) =>
          input.formName === 'address'
      );
  });

  lightDataSelected = computed(() => {
      if (!this.isLightVersion()) return this.dataSelected();
      return [];
  });

  constructor(
      private _title: Title,
      private _loaderService: LoaderService,
      private _listService: ListsService,
      private _securityService: SecurityService,
      private _router: Router,
      private _routeService: RouteService,
      private _helpService: HelpService,
      public cdr: ChangeDetectorRef
  ) {
      this._title.setTitle('Onboarding Digital | Datos extra');

      this.form = new FormGroup({
          asotip: new FormControl(null, Validators.required),
          location: new FormControl(null, Validators.required),
          address: new FormControl(null, Validators.required),
          neighborhood: new FormControl(null, Validators.required),
          zone: new FormControl(null, Validators.required),
      });

      this.nextRoute.set(this._routeService.getNextRoute(this._router.url) ?? '');

      this.form.get('asotip')?.valueChanges.subscribe((value: string | number) => {
          this.handleAsotipChange(value);
      });

      const lightVersion = localStorage.getItem('lightVersion');
      this.isLightVersion.set(lightVersion === 'true');

      // Efecto para recálculo de validación
      effect(() => {
          this.isValidData();
      });
  }

  private validateLightVersion(): boolean {
      if (!this.form.valid) {
          return false;
      }

      return this.validateObjectAttributes(this.defaultData(), ['income']);
  }

  private validateObjectAttributes(obj: Record<string, unknown> | null | undefined, keys: string[]): boolean {
      if (!obj) {
          return false;
      }

      for (const key of keys) {
          const value = coerceSelectRawValue(obj[key]);

          if (
              value === null ||
              value === undefined ||
              (typeof value === 'string' && value.trim() === '')
          ) {
              return false;
          }

          if (key === 'income' && !isMainIncomeInRange(value)) {
              return false;
          }
      }

      return true;
  }

  private validateAutomaticDebitData(data: Record<string, unknown>): boolean {
      if (!('automaticDebit' in data)) {
          return false;
      }

      if (data['automaticDebit'] === false) {
          return true;
      }

      if (data['automaticDebit'] === true && data['debitType'] === 'INTERNO') {
          return true;
      }

      if (data['automaticDebit'] === true && data['debitType'] === 'EXTERNO') {
          return this.validateObjectAttributes(data, [
              'bank',
              'accountNumber',
              'accountType',
              'debitDay',
          ]);
      }

      return false;
  }

  validateAllData(): boolean {
      if (this.isLightVersion()) {
          return this.validateLightVersion();
      }

      const currentData = this.defaultData();
      const currentAsotipId = this.asotipId();

      if (!this.form.valid) {
          return false;
      }

      if (currentAsotipId === 1) {
          if (!this.validateObjectAttributes(currentData, this.getRequiredFieldsForAsotip(1))) {
              return false;
          }
          return this.validateAutomaticDebitData(currentData);
      }

      if (currentAsotipId === 2) {
          return this.validateObjectAttributes(currentData, this.getRequiredFieldsForAsotip(2));
      }

      if (currentAsotipId === 3) {
          return this.validateObjectAttributes(currentData, this.getRequiredFieldsForAsotip(3));
      }

      if (currentAsotipId === 5) {
          return this.validateObjectAttributes(currentData, this.getRequiredFieldsForAsotip(5));
      }

      return true;
  }

  async ngOnInit() {
      const authRoute = localStorage.getItem('route');
      if (!authRoute) {
          this._router.navigateByUrl('');
      }
      const currentUrl = this._router.url;
      if (authRoute && currentUrl !== authRoute) {
          await this._router.navigateByUrl(authRoute);
      } else {
          this._helpService.show();
          this.getAsotip();
      }
  }

  ngAfterViewInit(): void {
      if (!this.isCheckData) {
          this.getData();
      } else {
          setTimeout(() => {
              this.getDataAddress();
              setTimeout(() => {
                  this.validateAllData();
              }, 500);
          }, 100);
      }
      this._loaderService.hide();
  }

  ngOnDestroy(): void {
      this.fieldValidationMap.clear();
  }

  getData() {
      this.getDataAddress();
  }

  private initializeFieldValidation(fieldName: string, value: any, isValid: boolean = true) {
      this.fieldValidationMap.set(fieldName, {
          originalValue: value,
          currentValue: value,
          isDirty: false,
          isValid: isValid,
          hasChanged: false
      });
  }

  private updateFieldValidation(fieldName: string, newValue: any, isValid: boolean) {
      const state = this.fieldValidationMap.get(fieldName);
      if (!state) {
          this.initializeFieldValidation(fieldName, newValue, isValid);
          return;
      }

      const hasChanged = newValue !== state.originalValue;
      const isDirty = hasChanged || (newValue !== state.currentValue);

      this.fieldValidationMap.set(fieldName, {
          ...state,
          currentValue: newValue,
          isDirty: isDirty,
          isValid: isValid,
          hasChanged: hasChanged
      });

      if (!hasChanged && newValue === state.originalValue && isValid) {
          this.fieldValidationMap.set(fieldName, {
              ...state,
              currentValue: newValue,
              isDirty: false,
              isValid: true,
              hasChanged: false
          });
      }

      this.validationTick.update((tick) => tick + 1);
      this.isValidData();
  }

  private updateLightVersionIncomeConfig(value: any) {
      const normalizedIncome = String(normalizeFieldValue(value, 'number') ?? '');
      this.lightVersionIncomeConfig = {
          ...this.lightVersionIncomeConfig,
          defaultValue: normalizedIncome,
          enabled: !hasPreloadedValue(normalizedIncome),
          originalValue: normalizedIncome || null,
          isDirty: false,
          isValid: hasPreloadedValue(normalizedIncome)
      };
      this.initializeFieldValidation('income', normalizedIncome, hasPreloadedValue(normalizedIncome));
  }

  private applyPreloadedFieldConfigs(data: Record<string, unknown>): void {
      this.dataInputs.update((inputs: any) =>
          inputs.map((input: any) => {
              const rawValue = resolveRawFieldValue(data, String(input.formName));
              const config = buildPreloadedConfig(input, rawValue, input.type);
              return { ...config, showBlocker: input.showBlocker ?? true };
          })
      );

      this.dataSelected.update((selects: any) =>
          selects.map((select: any) =>
              buildPreloadedSelectConfig(select, data[select.formName], select.options, data)
          )
      );
  }

  private syncAddressFormFromData(data: Record<string, unknown>): void {
      const address = resolveRawFieldValue(data, 'address');

      this.form.patchValue({
          location: data['location'] ?? null,
          address: address ?? null,
          neighborhood: data['neighborhood'] ?? null,
          zone: data['zone'] ?? null,
          asotip: data['asotip'] ?? null,
      }, { emitEvent: false });

      if (hasPreloadedValue(address)) {
          this.defaultData.update((current) => ({
              ...current,
              address,
          }));
      }

      this.validationTick.update((tick) => tick + 1);
  }

  private initializeFieldValidationsFromData(data: Record<string, unknown>): void {
      this.dataInputs().forEach((input: any) => {
          const value = normalizeFieldValue(
              resolveRawFieldValue(data, String(input.formName)),
              input.type
          );
          this.initializeFieldValidation(input.formName, value, hasPreloadedValue(value));
      });

      this.dataSelected().forEach((select: any) => {
          const value = coerceSelectRawValue(data[select.formName]);
          this.initializeFieldValidation(select.formName, value, hasPreloadedValue(value));
      });

      const asotip = Number(data['asotip']);
      for (const fieldName of this.getRequiredFieldsForAsotip(asotip)) {
          if (this.fieldValidationMap.has(fieldName)) continue;
          const value = coerceSelectRawValue(data[fieldName]);
          this.initializeFieldValidation(fieldName, value, hasPreloadedValue(value));
      }

      this.validationTick.update((tick) => tick + 1);
  }

  getDataAddress() {
      if (this.isCheckData && this.dataLastOnboarding) {
          this.defaultData.set(this.dataLastOnboarding);
          this.asotipId.set(this.defaultData().asotip);
          this.originalValues = { ...this.dataLastOnboarding };
          this.fieldValidationMap.clear();

          if (this.isLightVersion()) {
              this.updateLightVersionIncomeConfig(this.defaultData().income);
          }

          this.initializeFieldValidationsFromData(this.dataLastOnboarding);
          this.applyPreloadedFieldConfigs(this.dataLastOnboarding);
          this.syncAddressFormFromData(this.dataLastOnboarding);

          return;
      }

      this._securityService.getAddressData().subscribe({
          next: (response: any) => {
              if (response) {
                  this.defaultData.set(response.addressData);
                  this.asotipId.set(this.defaultData().asotip);
                  this.originalValues = { ...response.addressData };
                  this.fieldValidationMap.clear();

                  if (this.isLightVersion()) {
                      this.updateLightVersionIncomeConfig(response.addressData.income);
                  }

                  this.initializeFieldValidationsFromData(response.addressData);
                  this.applyPreloadedFieldConfigs(response.addressData);
                  this.syncAddressFormFromData(response.addressData);
              }
          },
          error: (error: any) => {
              console.error("ERROR: ", error);
          }
      });
  }

  getAsotip() {
      this._listService.getAsotip().subscribe({
          next: (response: any) => {
              this.dataSelected().at(0)?.options.set(response);
              if (Object.keys(this.defaultData()).length > 0) {
                  this.applyPreloadedFieldConfigs(this.defaultData());
                  this.cdr.detectChanges();
              }
          },
          error: (error: any) => {
              console.error("ERROR: ", error);
          }
      });
  }

  setLocation(location: any) {
      const nextLocation = location && typeof location === 'object' && 'value' in location
          ? location.value
          : location;

      if (this.defaultData().location != nextLocation) {
          this.form.get('location')!.setValue(nextLocation);

          this.defaultData.update(currentData => ({
              ...currentData,
              location: nextLocation
          }));

          this.validationTick.update((tick) => tick + 1);
      }
  }

  handleValue(event: { formName: string, value: string | number, isValid?: boolean, isDirty?: boolean }) {
      const currentValue = this.form.get(event.formName)?.value;

      if (!hasPreloadedValue(event.value) && hasPreloadedValue(currentValue) && event.isDirty !== true) {
          return;
      }

      if (event.formName == 'asotip') {
          this.asotipId.set(Number(event.value) ?? null);
      }

      if (event.formName === 'address') {
          this.onAddressBlur(String(event.value ?? ''));
          return;
      }

      const nextValue = event.formName === 'zone' ? this.toZoneId(event.value) : event.value;
      this.form.get(event.formName)?.setValue(nextValue);
      this.defaultData.update(currentData => ({
          ...currentData,
          [event.formName]: nextValue
      }));

      this.validationTick.update((tick) => tick + 1);
  }

  private patchAddressValue(value: string) {
      const currentFormValue = this.form.get('address')?.value;
      const currentConfig = this.dataInputs().find((input) => input.formName === 'address');

      this.form.get('address')?.setValue(value, { emitEvent: false });
      this.defaultData.update((currentData) => (
          currentData.address === value ? currentData : {
              ...currentData,
              address: value
          }
      ));

      if (currentConfig?.defaultValue !== value) {
          this.dataInputs.update((inputs) =>
              inputs.map((input) =>
                  input.formName === 'address' ? { ...input, defaultValue: value } : input
              )
          );
      }

      if (currentFormValue !== value) {
          this.validationTick.update((tick) => tick + 1);
      }
  }

  private onAddressBlur(rawValue: string) {
      const normalized = collapseConsecutiveSpaces(rawValue);
      this.resetAddressConfirmationIfChanged(normalized);
      this.patchAddressValue(normalized);

      if (!hasMinAddressLength(normalized)) {
          this.lastProcessedAddress = null;
          this.lastAddressValidation = null;
          this.allowAddres = false;
          return;
      }

      if (this.lastProcessedAddress === normalized && this.lastAddressValidation) {
          return;
      }

      this.normalizeAddressWithIA(normalized);
  }

  private shouldClearInvalidAddress(response: any): boolean {
      if (!response) {
          return false;
      }

      return response.cleared === true || response.ok === false;
  }

  private normalizeAddressWithIA(normalized: string): Promise<any> {
      if (this.inflightNormalization && this.inflightAddress === normalized) {
          return this.inflightNormalization;
      }

      this.inflightAddress = normalized;
      this.inflightNormalization = new Promise((resolve) => {
          this._securityService.checkAddress(normalized).subscribe({
              next: (response: any) => {
                  if (this.shouldClearInvalidAddress(response)) {
                      this.clearInvalidAddress();
                      resolve(response);
                      return;
                  }

                  if (response.tooShort) {
                      this.lastProcessedAddress = null;
                      this.lastAddressValidation = null;
                      resolve(response);
                      return;
                  }

                  this.lastProcessedAddress = normalized;
                  this.lastAddressValidation = response.result ?? null;
                  this.defaultData.update((currentData) => ({
                      ...currentData,
                      address: response.address ?? normalized,
                      addressFixed: response.addressFixed
                  }));
                  resolve(response);
              },
              error: (error: any) => {
                  console.error('Error normalizando dirección', error);
                  toast.error('No se pudo validar la dirección. Intenta de nuevo.');
                  resolve(null);
              }
          });
      }).finally(() => {
          if (this.inflightAddress === normalized) {
              this.inflightAddress = null;
              this.inflightNormalization = null;
          }
      });

      return this.inflightNormalization;
  }

  private clearInvalidAddress() {
      this.patchAddressValue('');
      this.lastProcessedAddress = null;
      this.lastAddressValidation = null;
      this.allowAddres = false;
      this.confirmedAddress = null;
      this.defaultData.update((currentData) => ({
          ...currentData,
          address: '',
          addressFixed: '',
          isAddressConfirmed: false
      }));
      toast.error('Por favor verifica la dirección. Escribela nuevamente.');
  }

  private isFieldValid(fieldName: string, value: any): boolean {
      const normalized = coerceSelectRawValue(value);

      const control = this.form.get(fieldName);
      if (control) {
          if (control.validator) {
              const errors = control.validator(control);
              return errors === null;
          }
      }

      if (normalized === undefined || normalized === null || normalized === '') {
          return false;
      }
      return true;
  }

  private normalizeFormFields(formFields: Record<string, unknown>): Record<string, unknown> {
      return Object.fromEntries(
          Object.entries(formFields).map(([key, value]) => [key, coerceSelectRawValue(value)])
      );
  }

  handleEvent(data: any) {
      const {
          _isValid,
          _formName,
          _field,
          _isDirty,
          address: _address,
          neighborhood: _neighborhood,
          location: _location,
          ...formFields
      } = data;
      const normalizedFields = this.normalizeFormFields(formFields);
      const current = this.defaultData();

      if (normalizedFields['zone'] !== undefined) {
          const zoneId = this.toZoneId(normalizedFields['zone']);
          normalizedFields['zone'] = zoneId;
          this.form.get('zone')?.setValue(zoneId, { emitEvent: false });
      }

      const income = normalizedFields['income'];
      if (current['income'] !== income && income != '' && income != null) {
          this._securityService.calculateQuota(income as string | number).subscribe({
              next: (response: any) => {
                  this.showQuotaModal.set(true);
                  this.defaultData.update(currentData => ({
                      ...currentData,
                      calculatedQuota: Number(response.data).toLocaleString('es-ES')
                  }));
              },
              error: (error: any) => {
                  console.error('Error', error);
              }
          });
      }

      this.defaultData.update(currentData => ({
          ...currentData,
          ...normalizedFields
      }));

      this.validationTick.update((tick) => tick + 1);
  }

  private handleAsotipChange(value: any) {
      this.asotipId.set(Number(value) ?? null);
      const formValues = this.form.getRawValue();
      this.defaultData.update((current) => {
          const next: Record<string, unknown> = {
              ...current,
              asotip: Number(value) ?? current['asotip'],
          };

          for (const key of ['location', 'address', 'neighborhood', 'zone']) {
              const formValue = formValues[key];
              if (hasPreloadedValue(formValue) || formValue === 0) {
                  next[key] = formValue;
              }
          }

          return next;
      });
      this.validationTick.update((tick) => tick + 1);
  }

  private getRequiredFieldsForAsotip(asotipId: number): string[] {
      switch (asotipId) {
          case 1:
              return ['indact', 'academic', 'specialProtecionProgram', 'econmicActivity', 'income', 'codpro', 'zone'];
          case 2:
              return ['since', 'codpro', 'academic', 'company', 'specialProtecionProgram', 'income', 'zone'];
          case 3:
              return ['fonpen', 'since', 'codpro', 'academic', 'specialProtecionProgram', 'income', 'zone'];
          case 5:
              return ['indact', 'codpro', 'academic', 'specialProtecionProgram', 'income', 'zone'];
          default:
              return [];
      }
  }

  async onSubmit() {
      if (!this.isValidData()) {
          toast.error('Faltan datos por completar');
          return;
      }

      const normalized = collapseConsecutiveSpaces(this.form.get('address')?.value);
      this.patchAddressValue(normalized);

      if (!hasMinAddressLength(normalized)) {
          toast.error('Ingresa una dirección válida de al menos 8 caracteres.');
          return;
      }

      if (this.shouldSkipAddressCheck()) {
          this.saveData();
          return;
      }

      let response: any = null;
      if (this.lastProcessedAddress !== normalized || !this.lastAddressValidation) {
          this._loaderService.show();
          response = await this.normalizeAddressWithIA(normalized);
          this._loaderService.hide();
          if (!response || response.cleared || response.tooShort) {
              return;
          }
      }

      const validation = response?.result ?? this.lastAddressValidation;
      if (validation === 'INCORRECTA') {
          this.allowAddres = false;
          this.isValidAddress.set(true);
          return;
      }

      this.defaultData.update((currentData) => ({
          ...currentData,
          isAddressConfirmed: false
      }));
      this.saveData();
  }

  private buildAddressPayload(): Record<string, unknown> {
      const formValues = this.form.getRawValue();
      return {
          ...this.defaultData(),
          asotip: formValues.asotip ?? this.defaultData().asotip,
          location: formValues.location ?? this.defaultData().location,
          address: formValues.address ?? this.defaultData().address,
          addressFixed: this.defaultData().addressFixed,
          neighborhood: formValues.neighborhood ?? this.defaultData().neighborhood,
          zone: this.toZoneId(formValues.zone ?? this.defaultData().zone),
      };
  }

  saveData(): Promise<void> {
      this._loaderService.show();
      const payload = this.buildAddressPayload();
      this.defaultData.set(payload);
      return new Promise((resolve, reject) => {
          this._securityService.setAddressData(payload).subscribe({
              next: () => {
                  if (!this.isCheckData) {
                      this.showWowSignal.set(true);
                  }
                  this._loaderService.hide();
                  resolve();
              },
              error: (err: any) => {
                  this._loaderService.hide();
                  if (!this.shouldSkipAddressCheck()) {
                      this.isValidAddress.set(true);
                  } else {
                      toast.error('No se pudo guardar la dirección. Intenta de nuevo.');
                  }
                  reject(err);
              }
          });
      });
  }

  hideWow() {
      this._loaderService.hide();
      localStorage.setItem('route', this.nextRoute());
      this.showWowSignal.set(false);
      this._router.navigateByUrl(this.nextRoute());
  }

  backValidateAddress() {
      this.resetAddressConfirmation();
      this.lastProcessedAddress = null;
      this.lastAddressValidation = null;
      this.isValidAddress.set(false);
      this.addressInput?.nativeElement?.focus();
      this.addressInput?.nativeElement?.select();
  }

  onCheckboxChange(event: any) {
      this.isChecked = event.target.checked;
  }

  validateAddress() {
      this._securityService.confirmAddress(true).subscribe({
          next: () => {
              const address = String(this.form.get('address')?.value ?? '').trim();
              this.confirmedAddress = address;
              this.allowAddres = true;
              this.isChecked = false;
              this.defaultData.update((currentData) => ({
                  ...currentData,
                  isAddressConfirmed: true
              }));
              this.isValidAddress.set(false);
              this.saveData();
          },
          error: () => {
              toast.error('No se pudo confirmar la dirección. Intenta de nuevo.');
          }
      });
  }

  private shouldSkipAddressCheck(): boolean {
      const currentAddress = String(this.form.get('address')?.value ?? '').trim();
      const confirmedMatches =
          this.confirmedAddress !== null &&
          this.confirmedAddress === currentAddress;

      return this.allowAddres === true ||
          this.defaultData()?.isAddressConfirmed === true ||
          confirmedMatches;
  }

  private resetAddressConfirmation(): void {
      this.allowAddres = false;
      this.confirmedAddress = null;
      this.isChecked = false;
      this.defaultData.update((currentData) => ({
          ...currentData,
          isAddressConfirmed: false
      }));
  }

  private resetAddressConfirmationIfChanged(value: string | number): void {
      if (this.confirmedAddress === null && !this.allowAddres) {
          return;
      }

      const nextAddress = collapseConsecutiveSpaces(value);
      if (nextAddress !== this.confirmedAddress) {
          this.resetAddressConfirmation();
      }
  }

  onChildDataChange(data: any) {
      this.defaultData.update(currentData => ({
          ...currentData,
          ...data
      }));
      this.isValidData();
  }

  getFieldState(fieldName: string): FieldValidationState | undefined {
      return this.fieldValidationMap.get(fieldName);
  }

  getAsotipLabel(value: number): string {
      const asotipOptions = [
          { value: 1, label: 'Independiente' },
          { value: 2, label: 'Asociado' },
          { value: 3, label: 'Pensionado' },
          { value: 5, label: 'Agrícola' }
      ];
      const option = asotipOptions.find(opt => opt.value === value);
      return option ? option.label : value.toString();
  }

  getLocationLabel(value: number): string {
      return value ? `Ubicación ${value}` : 'No especificado';
  }

  getZoneLabel(value: number): string {
      const zoneOptions = [
          { value: 1, label: 'Rural' },
          { value: 2, label: 'Urbana' }
      ];
      const option = zoneOptions.find(opt => opt.value == value);
      return option ? option.label : value.toString();
  }

  private toZoneId(value: unknown): number | null {
      if (value === null || value === undefined || value === '') {
          return null;
      }
      const zoneId = Number(value);
      return zoneId === 1 || zoneId === 2 ? zoneId : null;
  }
}
